/*
 * Copyright (C) 2022 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import {InPlaceEdit} from '@instructure/ui-editable'
import React, {useEffect, useState} from 'react'
import type {ChangeEvent, LegacyRef, ReactNode} from 'react'
import {Text} from '@instructure/ui-text'
import {useScope as createI18nScope} from '@canvas/i18n'
import {type ButtonProps, IconButton} from '@instructure/ui-buttons'
import {IconEditLine} from '@instructure/ui-icons'

const I18n = createI18nScope('internal-settings')

export type EditableCodeValueProps = {
  value: string
  name?: string
  secret?: boolean
  readonly?: boolean
  screenReaderLabel?: string
  placeholder?: ReactNode
  onValueChange: (newValue: string) => void
}

export const EditableCodeValue = (props: EditableCodeValueProps) => {
  const [mode, setMode] = useState<'view' | 'edit'>('view')
  const [editorValue, setEditorValue] = useState(props.value)

  useEffect(() => {
    setEditorValue(props.value)
  }, [props.value])

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEditorValue(event.target?.value)
  }

  const renderEditButton = ({
    isVisible,
    readOnly,
    ...buttonProps
  }: ButtonProps & {
    isVisible: boolean
    readOnly: boolean
  }) => {
    if (readOnly) return null

    return (
      // @ts-expect-error InstUI component prop type mismatch
      <IconButton
        size="small"
        screenReaderLabel={
          props.screenReaderLabel ||
          (props.name
            ? I18n.t(`Edit value for "%{name}"`, {name: props.name})
            : I18n.t('Edit value'))
        }
        withBackground={false}
        withBorder={false}
        {...buttonProps}
      >
        {isVisible ? IconEditLine : null}
      </IconButton>
    )
  }

  const renderView = () =>
    props.placeholder || (
      <Text>
        <code style={{wordBreak: 'break-word'}}>{props.secret ? '*'.repeat(24) : props.value}</code>
      </Text>
    )

  const renderEdit = ({
    onBlur,
    editorRef,
  }: {
    onBlur: () => void
    editorRef: LegacyRef<HTMLInputElement>
  }) => (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore InstUI Text component used as input with incompatible event handler types
    <Text
      as="input"
      name={(props.name ? `${props.name} ` : '') + 'value'}
      type={props.secret ? 'password' : 'text'}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - InstUI Text component event handler type mismatch
      onChange={handleChange}
      onBlur={onBlur}
      autocomplete="off"
      value={editorValue}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - InstUI Text elementRef prop type mismatch
      elementRef={editorRef}
    />
  )

  return (
    <div>
      <InPlaceEdit
        renderViewer={renderView}
        renderEditor={renderEdit}
        // @ts-expect-error InstUI component prop type mismatch
        renderEditButton={renderEditButton}
        readOnly={props.readonly}
        mode={mode}
        // @ts-expect-error InstUI component prop type mismatch
        onChangeMode={setMode}
        value={editorValue}
        inline={false}
        onChange={props.onValueChange}
      />
    </div>
  )
}
